'use strict';
import filter from 'lodash/filter';
import omit from 'lodash/omit';
import {isCordovaBuild, isWebBuild} from '../../config/constants';

module.exports = [
  '$injector',
  '$permissions',
  'ContextMenuServiceImpl',
  $contextMenu];

function $contextMenu($injector, $permissions, ContextMenuServiceImpl) {
  function getI18nOptions(options) {
    if (!options.actions) throw new Error('Called Context Menu with no actions specified!');
    const i18nOptions = {
      actions: new Array(options.actions.length),
      titleText: assignText(options, 'titleText', 'titleMessageId', 'titleMessageParams'),
      cancelText: assignText(options, 'cancelText', 'cancelMessageId')
      || assignText({code: 'context.defaults.cancel.label'}, null, 'code')
    };

    for (let i = 0; i < options.actions.length; i++) {
      if (options.actions[i].messageId) {
        const action = omit(options.actions[i], ['messageId']);
        action.label = assignText(options.actions[i], 'label', 'messageId', 'messageParams');
        i18nOptions.actions.push(action);
      } else {
        i18nOptions.actions.push(options.actions[i]);
      }
    }

    return i18nOptions;
  }

  function assignText(object, text, messageId, messageParams) {
    if (object[messageId]) {
      return translate(object[messageId], object[messageParams] || null);
    } else if (object[text]) {
      return object[text];
    } else {
      console.warn('called $contextMenu.show() with a missing label or message ID', object);
    }
  }

  function translate(messageId, messageParams) {
    try {
      return $injector.get('$translate').instant(messageId, messageParams || null);
    } catch (errors) {
      console.warn('failed to retrieve $translate service or message ID ' + messageId, errors);
      return messageId;
    }
  }

  /**
   * Show the options found after all promises in the actions list resolve.
   * @param {Object} options
   * @param {string} options.titleMessageId
   * @param {Object} [options.titleMessageParams]
   * @param {string} [options.cancelMessageId]
   * @param {Array.<(Promise.<ContextAction>|*)>} options.actions
   * @returns {*}
   */
  function showByPermission(options) {
    return Promise.all(options.actions).then(function (allowedActions) {
      options.actions = filter(allowedActions);
      return ContextMenuServiceImpl.show(getI18nOptions(options));
    })
  }

  /**
   * Convenience method to add an action without a permission requirement.
   * @param {Array.<(Promise.<ContextAction>|*)>} checkActions
   * @param {PermissionAction} ACTION
   */
  function addAction(checkActions, ACTION) {
    checkActions.push(Promise.resolve(ACTION.action));
  }

  /**
   * Convenience method to add an action without a permission requirement.
   * @param {Array.<(Promise.<ContextAction>|*)>} checkActions
   * @param {PermissionAction} ACTION
   */
  function addWebBuildAction(checkActions, ACTION) {
    if (isWebBuild()) {
      checkActions.push(Promise.resolve(ACTION.action));
    }
  }

  /**
   * Convenience method to add an action without a permission requirement.
   * @param {Array.<(Promise.<ContextAction>|*)>} checkActions
   * @param {PermissionAction} ACTION
   */
  function addCordovaBuildAction(checkActions, ACTION) {
    if (isCordovaBuild()) {
      checkActions.push(Promise.resolve(ACTION.action));
    }
  }

  /**
   * Add an action to the context menu if the user has the ACTION's permission in the specified organization
   * @param {Array.<(Promise.<ContextAction>|*)>} checkActions
   * @param {string} orgId - Organization ID
   * @param {PermissionAction} ACTION
   */
  function addActionByOrg(checkActions, orgId, ACTION) {
    checkActions.push($permissions.checkOrganizationPermission(orgId, ACTION.permission).then(function (hasPermission) {
      if (hasPermission) return ACTION.action;
    }));
  }

  /**
   * Add an action to the context menu if the user has the ACTION's permission in the specified project
   * @param {Array.<(Promise.<ContextAction>|*)>} checkActions
   * @param {string} projectId
   * @param {PermissionAction} ACTION
   */
  function addActionByProject(checkActions, projectId, ACTION) {
    checkActions.push($permissions.checkProjectPermission(projectId, ACTION.permission).then(function (hasPermission) {
      if (hasPermission) return ACTION.action;
    }));
  }

  // TODO: add milestone version

  /**
   * Add an action to the context menu if the user has the ACTION's permission in the specified task
   * @param {Array.<(Promise.<ContextAction>|*)>} checkActions
   * @param {string} taskId
   * @param {PermissionAction} ACTION
   */
  function addActionByTask(checkActions, taskId, ACTION) {
    checkActions.push($permissions.checkTaskPermission(taskId, ACTION.permission).then(function (hasPermission) {
      if (hasPermission) return ACTION.action;
    }));
  }

  /**
   * Add an action to the context menu if the user has the ACTION's permission in the specified asset task
   * @param {Array.<(Promise.<ContextAction>|*)>} checkActions
   * @param {string} assetTaskId
   * @param {PermissionAction} ACTION
   */
  function addActionByAssetTask(checkActions, assetTaskId, ACTION) {
    checkActions.push($permissions.checkAssetTaskPermission(assetTaskId, ACTION.permission).then(function (hasPermission) {
      if (hasPermission) return ACTION.action;
    }));
  }

  /**
   * Methods:
   *
   * show(options)
   * properties:
   *   {Object} options.actions - each action is an option with a label and an action (function).
   *      optionally you many supply the execution context and arguments, but the first argument will always be the menu object.
   *      also, you may flag an item as important by setting important: true. Ionic only supports 1 important item so the first one wins.
   *   {string} options.titleText The title to show on the action sheet.
   *   {string=} options.cancelText the text for a 'cancel' button on the action sheet.
   *   {Function=} options.cancelFunction Called if the cancel button is pressed, the backdrop is tapped or the hardware back button is pressed.
   *
   *   // TODO: Not yet implemented
   *   {boolean=} cancelOnStateChange Whether to cancel the actionSheet when navigating to a new state. Default true.
   *
   * returns a promise that resolves when the menu is closed or is rejected when the menu is cancelled.
   *
   *
   * hide(value)
   *   resolves the current menu with the specified value
   *
   * cancel(value)
   *   rejects the current menu with the specified value
   */
  return {
    addAction,
    addWebBuildAction,
    addCordovaBuildAction,
    addActionByOrg,
    addActionByProject,
    addActionByTask,
    addActionByAssetTask,
    showByPermission,
    show: options => ContextMenuServiceImpl.show(getI18nOptions(options)),
    hide: ContextMenuServiceImpl.hide,
    cancel: ContextMenuServiceImpl.cancel
  };
}

/**
 * Defines an action to be displayed by the context menu.
 * @typedef {Object} ContextAction
 * @property {string} messageId
 * @property {Object} [messageParams]
 * @property {boolean} [important]
 * @property {Function} action
 */

/**
 * Defines an action and its required permission
 * @typedef {Object} PermissionAction
 * @property {string} permission
 * @property {ContextAction} action
 */



// WEBPACK FOOTER //
// ./src/js/core-components/context-menu/ContextMenuService.js